import React from 'react';
import {
    Button, FormGroup, Modal,
    ModalBody,
    ModalFooter,
} from 'reactstrap';
import { isEqual, mapValues } from 'lodash';
import { v4 as uuid } from 'uuid';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faTrash } from '@fortawesome/free-solid-svg-icons';
import { getCounty } from '@s20/common/util/location';
import { clone } from '@s20/common/util/object';
import { httpClient } from '@s20/services/httpClient';
import CommonContext, { ApiRoutes } from '../Common';
import {
    FlexBetweenRow,
    FlexCenterRow,
    FlexStartRow,
    FormDivider,
    FormGroupColumn,
    FormLabel,
    FormValidated,
    GroupedRow,
    Input,
    onReactSelectChanged,
    SmallOutlineButton,
    Textarea,
    toasty,
} from '../common/forms/FormElements';
import { Address } from '../address/Address';
import { OrganizationContact } from '../organization/Organization';
import ValidatedSelect from '../common/forms/ValidatedSelect';
import OrganizationContactForm from '../organization/OrganizationContactForm';
import AddressFormNew from '../address/AddressFormNew';
import { CountyLabel } from '../uscounties/CountyLabel';
import { getCompanyContactsOptions } from '../contact/Contact';
import { JobOverride } from './JobOverride';

export default class JobOverrideModal extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);

        this.addressFormRef = React.createRef();
        this.contactFormRef = React.createRef();

        this.state = {
            jobOverride: props.jobOverride,
            formValidated: false,
            loading: true,
            errors: {},
            validationMessage: '',
            show: false,
            showContactsForm: false,
            contactFormIsReadOnly: false,
            allowContactTypeEdit: false,
            selectedContact: null,
            saveDisabled: false,
            addJobDayOverrideFormValidated: false,
            job: props.jobOverride.job,

            weeks: [],
            usCounties: [],
            addressEditType: null, // from object const
            selectedAddressType: '',
            addressFormTitle: '',
            showTimesheetOverview: false,
            companyContactsOptions: [],
            tableRequired: {},
            meetingLocationRequired: {},
            jobLocationRequired: {},

            // Property on the job to update with the newly created/edited contact.
            // jobOverride.requestedById or formenId.
            selectedContactIdProp: null,
            dispatchPortLoading: false,
            dispatchPorts: [],
        };

        this.handleSubmit = this.handleSubmit.bind(this);
        this.onClose = this.onClose.bind(this);
        this.onChange = this.onChange.bind(this);
        this.onSelectChanged = this.onSelectChanged.bind(this);
        this.onRemoveOverrideButtonClick = this.onRemoveOverrideButtonClick.bind(this);
    }

    componentDidMount() {
        return this.populateState();
    }


    // #region CONTACTS
    onAddContact = (prop) => {
        const { job } = { ...this.state };
        const { setFormOpened } = this.props;

        setFormOpened(true);
        this.contactFormRef.current.resetForm();
        this.setState({
            selectedContact: {
                ...new OrganizationContact(),
                id: null /* new record */,
                companyId: job.contract.companyId /* this will be used to attach the new contact to the location. */,
            },
            selectedContactIdProp: prop,
            showContactsForm: true,
            allowContactTypeEdit: true,
        });
    };

    onAddLocation = (type) => {
        const { job } = this.state;

        const selectedAddress = new Address(job.contract.countryId, job.contract.countryCode);

        const addressFormTitle = `New ${type === 'meetingLocation' ? 'Meeting' : 'Job'
            } Location`;

        this.setState({
            selectedAddressType: type,
            addressFormTitle,
        });

        this.addressFormRef.current.open(
            selectedAddress,
            addressFormTitle,
            true,
            `Also use as ${type === 'meetingLocation' ? 'Job Location' : 'Meeting Location'
            }`,
        );
    };

    onChange = (e) => {
        if (!e || !e.target || !e.target.name) {
            return;
        }

        const changedPropertyName = e.target.name.split('.').pop();

        const { jobOverride } = this.state;

        jobOverride[changedPropertyName] = e.target.value;
        this.setState({ jobOverride });
    };

    isButtonDisabled = () => {
        const { jobOverride, dispatchPortLoading, job } = this.state;
        if (dispatchPortLoading) return true;
        const overrideDate = moment(jobOverride.overrideDate).toDate();
        const baseOverride = { ...new JobOverride(overrideDate) };
        if (!jobOverride.id) {
            baseOverride.dispatchPortId = job.dispatchPortId;
        }
        const currentOverride = mapValues(jobOverride, (value) => typeof value === 'undefined' ? null : value);
        delete currentOverride.job;
        currentOverride.id = null;
        currentOverride.jobId = 0;
        currentOverride.overrideDate = moment(currentOverride.overrideDate).toDate();

        currentOverride.weekOfDate = '';
        currentOverride.workOrderNumber = currentOverride.workOrderNumber === null ? '' : currentOverride.workOrderNumber;
        currentOverride.notes = currentOverride.notes === null ? '' : currentOverride.notes;
        currentOverride.jobTypeId = currentOverride.jobTypeId === null ? null : currentOverride.jobTypeId;
        currentOverride.startTime = currentOverride.startTime === '' ? null : currentOverride.startTime;
        currentOverride.jobTypeDescription = '';

        return isEqual(currentOverride, baseOverride);
    };

    onClose = () => {
        const { onClose, setFormOpened } = this.props;

        this.resetForm();
        setFormOpened(false);

        onClose();
    };

    onEditContact = async (prop, readOnly = false) => {
        const { jobOverride } = { ...this.state };

        const selectedContact = await httpClient.js(
            ApiRoutes.locationContact.byId(jobOverride[prop]),
        );
        this.contactFormRef.current.resetForm();

        this.setState({
            selectedContact: { ...selectedContact },
            showContactsForm: true,
            selectedContactIdProp: prop,
            allowContactTypeEdit: false,
            contactFormIsReadOnly: readOnly,
        });
    };

    onEditLocation = (type) => {
        const { jobOverride } = { ...this.state };
        const selectedAddress = { ...jobOverride[type] };
        const addressFormTitle = `Edit ${type === 'meetingLocation' ? 'Meeting' : 'Job'
            } Location`;

        this.setState({
            selectedAddress,
            selectedAddressType: type,
            addressFormTitle,
        });
        this.addressFormRef.current.open(
            selectedAddress,
            addressFormTitle,
            true,
            `Also use as ${type === 'meetingLocation' ? 'Job Location' : 'Meeting Location'
            }`,
        );
    };

    onOrganizationContactSaved = async () => {
        this.setState({ contactFormIsReadOnly: true });

        const {
            selectedContact, selectedContactIdProp, jobOverride, job,
        } = {
            ...this.state,
        };
        const isNew = parseInt(selectedContact.id ?? 0) <= 0;
        const result = await httpClient.andGetResponse(
            isNew ? httpClient.types.post : httpClient.types.put,
            isNew
                ? ApiRoutes.locationContact.create()
                : ApiRoutes.locationContact.update(selectedContact.id),
            { ...selectedContact },
            'Error Saving Contact',
        );

        if (result) {
            toasty.success('Contact saved.');

            // Make the contact the selected one - add returns just the Id, update returns object with id property.
            jobOverride[selectedContactIdProp] = result.id ?? result;

            if (this.props.onUpdateContacts) {
                // Refresh the contacts.
                const newOptions = await getCompanyContactsOptions(
                    job.contract.companyId,
                );

                this.props.onUpdateContacts(newOptions.companyContactsOptions);
            }

            // Update state.
            this.setState({
                jobOverride: { ...jobOverride },
                showContactsForm: false,
                saving: false,
            });
        } else {
            this.setState({ saving: false });
        }

        this.setState({ contactFormIsReadOnly: false });
    };
    // #endregion

    onRemoveOverrideButtonClick = () => {
        const { onClose, setFormOpened, jobOverride } = this.props;

        setFormOpened(false);

        if (jobOverride.id) {
            onClose({ ...jobOverride, isDeleted: true });
        } else {
            onClose();
        }
    };

    onSaveAddress = (address) => {
        const {
            jobOverride,
            selectedAddressType,
        } = this.state;

        jobOverride[selectedAddressType] = { ...address };

        if (address.duplicate) {
            if (selectedAddressType === 'jobLocation') {
                const mAddress = clone(address);
                mAddress.id = jobOverride.meetingLocation?.id ?? uuid();

                jobOverride.meetingLocation = mAddress;
            }

            if (selectedAddressType === 'meetingLocation') {
                const jAddress = clone(address);
                jAddress.id = jobOverride.jobLocation?.id ?? uuid();

                jobOverride.jobLocation = jAddress;
            }
        }

        this.setState({
            jobOverride,
        });
    };

    onSelectChanged = onReactSelectChanged;


    getDayHasAssignment = () => {
        const { job, jobOverride } = this.state;
        const indexName = `${jobOverride.overrideDay.toLowerCase()}HasAssignment`;

        return !!job.resources?.find((resource) => resource[indexName]);
    };

    handleSubmit = () => {
        const { jobOverride } = this.state;
        const { onClose, setFormOpened, setOverride } = this.props;

        // Revert uuid
        if (jobOverride.meetingLocation) {
            jobOverride.meetingLocation.id = typeof jobOverride.meetingLocation.id === 'string'
                ? null
                : jobOverride.meetingLocation.id;
        }

        if (jobOverride.jobLocation) {
            jobOverride.jobLocation.id = typeof jobOverride.jobLocation.id === 'string'
                ? null
                : jobOverride.jobLocation.id;
        }

        this.resetForm();
        setFormOpened(false);

        setOverride(true);
        onClose(jobOverride);
    };

    populateState = async () => {
        const { showTimesheetOverview, job, jobOverride } = { ...this.state };

        const [usCounties, tenantSettings, dispatchPorts] = await Promise.all([
            httpClient.js(ApiRoutes.USCounties.all()),
            httpClient.js(ApiRoutes.tenant.settings()),
            job.dispatchOfficeLocationId
                ? httpClient.js(ApiRoutes.typeAheads.dispatchPorts(job.dispatchOfficeLocationId))
                : Promise.resolve([]),
        ]);

        const defaultPort = dispatchPorts?.find((p) => p.isDefault) ?? null;
        const portShouldChange = jobOverride.dispatchPortId != null && !dispatchPorts?.some((p) => p.value === jobOverride.dispatchPortId);

        this.setState({
            loading: false,
            job: { ...job },
            showTimesheetOverview,
            usCounties,
            tenantSettings,
            dispatchPorts: dispatchPorts ?? [],
            jobOverride: portShouldChange ? { ...jobOverride, dispatchPortId: defaultPort?.value ?? null } : jobOverride,
        });
    };

    resetForm = () => this.setState({ formValidated: false });

    render() {
        const {
            jobOverride,
            addJobDayOverrideFormValidated,
            showContactsForm,
            contactFormIsReadOnly,
            allowContactTypeEdit,
            selectedContact,
            job,
            usCounties,
            dispatchPorts,
            dispatchPortLoading,
        } = this.state;

        const {
            open, isReadOnly, jobTypesOptions, companyContactsOptions,
        } = this.props;

        const countyWithMeetingLocation = getCounty(usCounties, {
            usCountyId: jobOverride.meetingLocation?.usCountyId,
            contractUnionWorkTypeId: job.contract.contractUnionWorkTypeId,
        });

        const countyWithJobLocation = getCounty(usCounties, {
            usCountyId: jobOverride.jobLocation?.usCountyId,
            contractUnionWorkTypeId: job.contract.contractUnionWorkTypeId,
        });

        return (
            <Modal
                className="modal-mobile-fullscreen modal-form"
                backdrop="static"
                keyboard={false}
                isOpen={open}
            >
                {Boolean(open) && (
                    <>
                        <div className="modal-header">
                            <FlexBetweenRow className="w-100">
                                <h5 className="mb-0">
                                    {isReadOnly ? 'View' : 'Edit'}
                                    {' '}
                                    Details for
                                    {' '}

                                    {jobOverride.overrideDay}
                                </h5>

                                <Button
                                    color="link"
                                    onClick={this.onClose}
                                >
                                    <FontAwesomeIcon
                                        icon={faTimes}
                                        size="lg"
                                    />
                                </Button>
                            </FlexBetweenRow>
                        </div>

                        <FormValidated
                            className="add-self-dispatch-time-form d-flex flex-column h-100 min-height-0"
                            id="addJobDayOverrideForm"
                            name="addJobDayOverrideForm"
                            ref={this.addTimeFormRef}
                            onSubmit={this.handleSubmit}
                            setIsValidated={(validated) => this.setState({ addJobDayOverrideFormValidated: validated })}
                            isValidated={addJobDayOverrideFormValidated}
                        >
                            <ModalBody>
                                <GroupedRow>
                                    <FormGroupColumn>
                                        <ValidatedSelect
                                            id="requestedBy"
                                            name="jobOverride.requestedById"
                                            label="Requested By"
                                            options={companyContactsOptions}
                                            value={
                                                (companyContactsOptions ?? []).find(
                                                    (x) => x.value
                                                        === jobOverride.requestedById,
                                                ) ?? ''
                                            }
                                            onChange={this.onSelectChanged}
                                            isDisabled={isReadOnly}
                                            showAddButton={!jobOverride?.requestedById}
                                            onAddButtonClick={() => this.onAddContact(
                                                'requestedById',
                                            )}
                                            showEditButton={!!jobOverride?.requestedById}
                                            onEditButtonClick={() => this.onEditContact(
                                                'requestedById',
                                            )}
                                            showViewButton={!!jobOverride?.requestedById}
                                            onViewButtonClick={() => this.onEditContact(
                                                'requestedById', true,
                                            )}
                                        />
                                    </FormGroupColumn>
                                    <FormGroupColumn>
                                        <ValidatedSelect
                                            id="foremen"
                                            name="jobOverride.foremenId"
                                            label="Foreman"
                                            options={companyContactsOptions}
                                            value={
                                                (companyContactsOptions ?? []).find(
                                                    (x) => x.value === jobOverride.foremenId,
                                                ) ?? ''
                                            }
                                            onChange={this.onSelectChanged}
                                            isDisabled={isReadOnly}
                                            showAddButton={!jobOverride?.foremenId}
                                            onAddButtonClick={() => this.onAddContact(
                                                'foremenId',
                                            )}
                                            showEditButton={!!jobOverride?.foremenId}
                                            onEditButtonClick={() => this.onEditContact(
                                                'foremenId',
                                            )}
                                            showViewButton={!!jobOverride?.foremenId}
                                            onViewButtonClick={() => this.onEditContact(
                                                'foremenId', true,
                                            )}
                                        />
                                    </FormGroupColumn>
                                </GroupedRow>
                                {!!job.dispatchOfficeLocationId && !!dispatchPorts.length && (
                                    <GroupedRow>
                                        <FormGroupColumn>
                                            <ValidatedSelect
                                                id="dispatchPortId"
                                                name="jobOverride.dispatchPortId"
                                                options={dispatchPorts}
                                                label="Dispatch Port"
                                                value={dispatchPorts.find((o) => o.value === jobOverride.dispatchPortId) ?? null}
                                                onChange={this.onSelectChanged}
                                                isLoading={dispatchPortLoading}
                                                disabled={dispatchPortLoading}
                                            />
                                        </FormGroupColumn>
                                    </GroupedRow>
                                )}
                                <FormDivider />
                                <GroupedRow>
                                    <FormGroupColumn>
                                        <Input
                                            label="Start Time"
                                            id="startTime"
                                            type="time"
                                            name="jobOverride.startTime"
                                            value={jobOverride.startTime ?? ''}
                                            onChange={this.onChange}
                                            readOnly={isReadOnly}
                                        />
                                    </FormGroupColumn>
                                    {job.contract.workOrderTypeId !== 3 && (
                                        <FormGroupColumn>
                                            <Input
                                                label="Work Order"
                                                id="workOrderNumber"
                                                name="jobOverride.workOrderNumber"
                                                value={jobOverride.workOrderNumber ?? ''}
                                                onChange={this.onChange}
                                                readOnly={isReadOnly}
                                                maxLength={44}
                                            />
                                        </FormGroupColumn>
                                    )}
                                    {job.contract.companyRequiresWorkRequest && (
                                        <FormGroupColumn>
                                            <Input
                                                label="Work Request"
                                                id="workRequest"
                                                name="jobOverride.workRequest"
                                                value={jobOverride.workRequest ?? ''}
                                                onChange={this.onChange}
                                                maxLength={44}
                                            />
                                        </FormGroupColumn>
                                    )}
                                    {job.contract.companyRequiresPurchaseOrderNumber && (
                                        <FormGroupColumn>
                                            <Input
                                                label="PO Number"
                                                id="purchaseOrderNumber"
                                                name="jobOverride.purchaseOrderNumber"
                                                value={jobOverride.purchaseOrderNumber ?? ''}
                                                onChange={this.onChange}
                                                maxLength={44}
                                            />
                                        </FormGroupColumn>
                                    )}
                                    <FormGroupColumn>
                                        <ValidatedSelect
                                            id="jobType"
                                            name="jobOverride.jobTypeId"
                                            label="What type of work is being done?"
                                            options={jobTypesOptions}
                                            value={
                                                (jobTypesOptions ?? []).find(
                                                    (x) => x.value === jobOverride.jobTypeId,
                                                ) ?? ''
                                            }
                                            onChange={this.onSelectChanged}
                                            isDisabled={isReadOnly}
                                        />
                                    </FormGroupColumn>
                                </GroupedRow>

                                <GroupedRow>
                                    <FormGroupColumn>
                                        <FormGroup>
                                            <FormLabel
                                                htmlFor="meetingLocation"
                                                label="Meeting Location"
                                            />
                                            {jobOverride.meetingLocation ? (
                                                <FlexStartRow>
                                                    <SmallOutlineButton
                                                        onClick={() => {
                                                            this.onEditLocation(
                                                                'meetingLocation',
                                                            );
                                                        }}
                                                        label="Edit"
                                                        disabled={isReadOnly}
                                                    />

                                                    <span className="form-control-plaintext ml-2">
                                                        {jobOverride.meetingLocation.address1}
                                                        {' '}

                                                        {jobOverride.meetingLocation.city}
                                                        {', '}

                                                        {jobOverride.meetingLocation.state}
                                                        {', '}

                                                        {jobOverride.meetingLocation.zip}
                                                        {' '}

                                                        (
                                                        <CountyLabel {...countyWithMeetingLocation} />
                                                        )
                                                    </span>
                                                </FlexStartRow>
                                            ) : (
                                                <Button
                                                    type="button"
                                                    color="secondary"
                                                    size="sm"
                                                    disabled={isReadOnly}
                                                    onClick={() => this.onAddLocation(
                                                        'meetingLocation',
                                                    )}
                                                >
                                                    Add Meeting Location
                                                </Button>
                                            )}
                                        </FormGroup>
                                    </FormGroupColumn>
                                    <FormGroupColumn>
                                        <FormGroup>
                                            <FormLabel
                                                htmlFor="jobLocation"
                                                label="Job Location"
                                            />
                                            {jobOverride.jobLocation ? (
                                                <FlexStartRow>
                                                    <SmallOutlineButton
                                                        onClick={() => {
                                                            this.onEditLocation(
                                                                'jobLocation',
                                                            );
                                                        }}
                                                        label="Edit"
                                                        disabled={isReadOnly}
                                                    />

                                                    <span className="form-control-plaintext ml-2">
                                                        {jobOverride.jobLocation.address1}
                                                        {' '}

                                                        {jobOverride.jobLocation.city}
                                                        {', '}

                                                        {jobOverride.jobLocation.state}
                                                        {', '}

                                                        {jobOverride.jobLocation.zip}
                                                        {' '}

                                                        (
                                                        <CountyLabel {...countyWithJobLocation} />
                                                        )
                                                    </span>
                                                </FlexStartRow>
                                            ) : (
                                                <Button
                                                    type="button"
                                                    color="secondary"
                                                    size="sm"
                                                    disabled={isReadOnly}
                                                    onClick={() => {
                                                        this.onAddLocation(
                                                            'jobLocation',
                                                        );
                                                    }}
                                                >
                                                    Add Job Location
                                                </Button>
                                            )}
                                        </FormGroup>
                                    </FormGroupColumn>
                                </GroupedRow>

                                <GroupedRow>
                                    <div className="col-md-auto col-sm-12 col-lg-auto col-xl-auto w-100">
                                        <Textarea
                                            formGroupClassName="w-100"
                                            label="Notes"
                                            id="notes"
                                            name="jobOverride.notes"
                                            value={jobOverride.notes ?? ''}
                                            onChange={this.onChange}
                                            placeholder="Enter notes to field staff here."
                                            maxLength="500"
                                            rows="5"
                                            readOnly={isReadOnly}
                                        />
                                    </div>
                                </GroupedRow>

                                {this.getDayHasAssignment() && (
                                    <GroupedRow>
                                        <div className="col-md-auto col-sm-12 col-lg-auto col-xl-auto w-100">
                                            <Textarea
                                                formGroupClassName="w-100"
                                                label="Reason for this change?"
                                                id="notes"
                                                name="jobOverride.reason"
                                                value={jobOverride.reason ?? ''}
                                                onChange={this.onChange}
                                                placeholder="Enter a reason for making this change. Assigned crew will be notified that changes have been made."
                                                maxLength="500"
                                                rows="5"
                                                readOnly={isReadOnly}
                                                error="A reason is required."
                                                required
                                            />
                                        </div>
                                    </GroupedRow>
                                )}
                            </ModalBody>

                            <ModalFooter>
                                {isReadOnly ? (
                                    <Button
                                        type="button"
                                        className="w-100 d-flex flex-row flex-nowrap align-items-center justify-content-center"
                                        color="primary"
                                        onClick={this.onClose}
                                        outline
                                    >
                                        Close
                                    </Button>
                                ) : (
                                    <>
                                        <FlexCenterRow className="w-100">
                                            <Button
                                                type="submit"
                                                name="addJobDayOverrideForm"
                                                className="w-100 d-flex flex-row flex-nowrap align-items-center justify-content-center"
                                                color="primary"
                                                disabled={this.isButtonDisabled()}
                                            >
                                                Save Override
                                            </Button>
                                        </FlexCenterRow>

                                        {Boolean(jobOverride.jobId) && (
                                            <FlexCenterRow className="w-100">
                                                <Button
                                                    title="Click to remove the override for this day"
                                                    color="outline-danger"
                                                    className="mt-3 cursor-pointer w-100"
                                                    onClick={this.onRemoveOverrideButtonClick}
                                                >
                                                    <FontAwesomeIcon
                                                        icon={faTrash}
                                                        className="mr-1"
                                                    />
                                                    Delete Override
                                                </Button>
                                            </FlexCenterRow>
                                        )}
                                    </>
                                )}
                            </ModalFooter>
                        </FormValidated>
                    </>
                )}

                <AddressFormNew
                    id="jobAddressOverrideForm"
                    ref={this.addressFormRef}
                    contractUsesPrevailingWage={job.contract.prevailingWage || job.contract.requirePaySchedule}
                    isUnionWork={job.contract.isUnionWork}
                    contractId={job.contractId}
                    workTypeId={job.contract?.contractUnionWorkTypeId}
                    countryId={job.contract.countryId}
                    countryCode={job.contract.countryCode}
                    onSaveCallback={(address) => this.onSaveAddress(address)}
                    timeZone={this.props.timeZone}
                />
                <OrganizationContactForm
                    ref={this.contactFormRef}
                    show={showContactsForm}
                    readOnly={contactFormIsReadOnly}
                    allowContactTypeEdit={allowContactTypeEdit}
                    contact={selectedContact}
                    onClose={() => {
                        this.setState({ showContactsForm: false });
                    }}
                    onChange={(e) => {
                        const { selectedContact } = { ...this.state };
                        selectedContact[e.target.name] = e.target.value;
                        this.setState({
                            newContact: { ...selectedContact },
                        });
                    }}
                    onCheckedChanged={(e) => {
                        const { selectedContact } = { ...this.state };
                        selectedContact[e.target.name] = e.target.checked;
                        this.setState({
                            newContact: { ...selectedContact },
                        });
                    }}
                    onContactTypeChanged={(items) => {
                        const { selectedContact } = this.state;
                        selectedContact.contactTypes = items.map(
                            (x) => x.value,
                        );
                        this.setState({
                            newContact: { ...selectedContact },
                        });
                    }}
                    onSaveCallback={this.onOrganizationContactSaved}
                />
            </Modal>
        );
    }
}
